/**
 * The slice of the Web Storage API the plans are kept through.
 *
 * The seven state hooks read and write through one of these rather than
 * `localStorage` itself, so the same code serves both places a user's plans
 * can live: the device, and (once they have an account) the account.
 */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * `localStorage`, looked up on every call.
 *
 * Touching `window.localStorage` can itself throw where site data is blocked,
 * so it happens inside the caller's own try/catch, as it always has.
 */
export const deviceStorage: KeyValueStorage = {
  getItem: (key) => localStorage.getItem(key),
  setItem: (key, value) => localStorage.setItem(key, value),
  removeItem: (key) => localStorage.removeItem(key),
};

/** Reads that never throw: site data blocked reads as nothing stored. */
export function safeGet(storage: KeyValueStorage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

/** Writes that report whether they landed, for the steps that depend on it. */
export function safeSet(storage: KeyValueStorage, key: string, value: string | null): boolean {
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function parseJson(raw: string | null): unknown {
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}

/** JSON with object keys sorted, so two equal values always compare equal. */
export function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_key, v: unknown) => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return v;
    const sorted: Record<string, unknown> = {};
    for (const k of Object.keys(v).sort()) sorted[k] = (v as Record<string, unknown>)[k];
    return sorted;
  });
}

export const sameJson = (a: unknown, b: unknown): boolean => stableStringify(a) === stableStringify(b);

export const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);
